import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { createQueryKeys, type ApiError } from '../core'
import { generationVersionsService } from '../generation-versions/generation-versions.service'
import { assetsService } from './assets.service'
import type { AssetAnalysisUpdate, ProjectAssetDto, UploadFiles, UploadResponseDto } from './assets.schemas'

export const assetsKeys = createQueryKeys('assets')

export const assetsQueries = {
  /** Every file of one project. */
  ofProject: (projectId: string) => queryOptions({ queryKey: assetsKeys.all({ projectId }), queryFn: () => assetsService.listAll(projectId) }),
}

/** `useProjectAssets(projectId)`: the files the server holds for a project. Pass `undefined` to hold off. */
export function useProjectAssets(projectId: string | undefined) {
  // Asked for once and kept: the list only changes when this app uploads, and an upload refreshes it (see `useUploadAssets`).
  return useQuery({ ...assetsQueries.ofProject(projectId ?? ''), enabled: Boolean(projectId), staleTime: Infinity, refetchOnWindowFocus: false, refetchOnReconnect: false })
}

/** `useUploadAssets().mutate({ projectId, files })`. A failed upload shows an error toast, and is never retried by itself. A good one refreshes the project's file list. */
export function useUploadAssets() {
  const queryClient = useQueryClient()
  return useMutation<UploadResponseDto, ApiError, { projectId: string; files: UploadFiles }>({
    mutationFn: ({ projectId, files }) => assetsService.upload(projectId, files),
    onSuccess: (_result, { projectId }) => queryClient.invalidateQueries({ queryKey: assetsKeys.all({ projectId }) }),
  })
}

/** `useUpdateAssetAnalyses().mutateAsync({ projectId, updates })`: saves edited analyses. The cached list takes what was sent. Never retried by itself. */
export function useUpdateAssetAnalyses() {
  const queryClient = useQueryClient()
  return useMutation<void, ApiError, { projectId: string; updates: AssetAnalysisUpdate[] }>({
    mutationFn: ({ projectId, updates }) => assetsService.updateAnalyses(projectId, updates),
    onSuccess: (_result, { projectId, updates }) => {
      queryClient.setQueryData<ProjectAssetDto[]>(assetsKeys.all({ projectId }), (list) =>
        list?.map((item) => {
          const update = updates.find((candidate) => candidate.id === item.id)
          return update ? { ...item, analysis: { ...item.analysis, status: item.analysis?.status ?? 'success', ...update.analysis } } : item
        }),
      )
    },
  })
}

/**
 * One stored file's bytes, for showing it. An asset only names where its file is kept (`fileUrl` is a storage path, not a web address), so
 * the file is downloaded from the project's files, once, and kept for a few minutes. A real web address (or a data / blob address from this
 * browser) is fetched as it is.
 */
export const projectFileQuery = (projectId: string, path: string) =>
  queryOptions({
    queryKey: assetsKeys.detail(`file:${projectId}:${path}`),
    queryFn: async (): Promise<Blob> => {
      if (/^(https?:|data:|blob:|\/)/.test(path)) return (await fetch(path)).blob()
      return generationVersionsService.downloadImage(projectId, path)
    },
    staleTime: Infinity,
    gcTime: 5 * 60_000,
    retry: false,
    refetchOnWindowFocus: false,
  })

/** `useProjectFile(projectId, path)`: the file's bytes, loading, or why it could not be had. Pass `undefined` to hold off. */
export function useProjectFile(projectId: string, path: string | undefined) {
  return useQuery({ ...projectFileQuery(projectId, path ?? ''), enabled: Boolean(path) })
}
